import * as React from "react";
import { Image, MessageSquareQuote, PencilLine, Wand2 } from "lucide-react";
import { Button } from "@/components/ui";
import { BriefBuilder, BriefBuilderProps } from "./BriefBuilder";
import { HelpCallout, HelpCards, HelpGuideProps, HelpSection } from "./HelpDialog";
import { RefineExamples } from "./RefineExamples";
import { SupportLine } from "./SupportLine";

const LEAVE_TO_THE_POST = [
  { source: "Player", fields: "Name, title, headshot, NMLS and compliance line" },
  { source: "Branding", fields: "Background and text colors, accent color, branch logo, disclaimer" },
  {
    source: "Review (review designs)",
    fields: "The quote with its best phrase highlighted, stars, reviewer name, platform",
  },
];

// Short enough to sit two to a line in the half-width card.
const EXAMPLES = [
  "frosted glass card over a home photo",
  "big bold headline, no photo",
  "overlap the headshot on the quote card",
  "highlight the best phrase",
];

const LEAVE_TO_THE_POST_CARD = {
  title: "Leave to the post",
  body: (
    <div className="space-y-2">
      <p>Filled in for each Player when they share, so you never ask for them.</p>
      <ul className="space-y-1">
        {LEAVE_TO_THE_POST.map(row => (
          <li key={row.source}>
            <strong className="text-slate-700">{row.source}</strong> · <span>{row.fields}</span>
          </li>
        ))}
      </ul>
    </div>
  ),
};

const BRIEF = [
  {
    title: "Be specific",
    body: (
      <div className="space-y-3">
        <p>Name the layout, imagery, type, mood, and where things sit on the card.</p>
        <div className="space-y-1.5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Try asking for</div>
          <ul className="flex flex-wrap gap-1.5">
            {EXAMPLES.map(example => (
              <li
                key={example}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-2.5 py-1 text-sm text-slate-700 shadow-xs"
              >
                <MessageSquareQuote className="size-3.5 shrink-0 text-primary" aria-hidden="true" />
                {example}
              </li>
            ))}
          </ul>
        </div>
      </div>
    ),
  },
  LEAVE_TO_THE_POST_CARD,
];

const BackgroundsLine: React.FC<HelpGuideProps & { refining: boolean }> = ({ onNavigate, refining }) => (
  <HelpSection title="Backgrounds" icon={Image}>
    <p className="text-sm text-slate-600">
      {refining
        ? "To change the photo itself, use the backgrounds under the slide rather than asking: pick from your library, upload one, or generate more with AI."
        : 'Background picks come from a library of ready, people-free photos, so they show up instantly. Ask for a kind of photo, like "a modern kitchen at golden hour", and use Generate more with AI on the slide when you want something new.'}{" "}
      <Button variant="link" size="inline" onClick={() => onNavigate("backgrounds")}>
        How backgrounds work
      </Button>
    </p>
  </HelpSection>
);

export const WhatCanIAskGuide: React.FC<HelpGuideProps & { builder: BriefBuilderProps }> = ({
  onNavigate,
  builder,
}) => {
  const { action } = builder;
  if (action.kind === "refine") {
    return (
      <>
        <HelpSection title="Refine it" icon={PencilLine}>
          <p className="text-sm text-slate-600">
            Ask for one change at a time, and choose one slide or all of them in the box first.
            {action.onPick ? " Tap an example to start from it." : ""}
          </p>
          <RefineExamples
            onPick={
              action.onPick &&
              (example => {
                action.onPick?.(example);
                builder.onDone();
              })
            }
          />
        </HelpSection>

        <HelpCards items={[LEAVE_TO_THE_POST_CARD]} />

        <BackgroundsLine onNavigate={onNavigate} refining />

        <HelpCallout>
          Nothing changes for players until you keep a version. Compare before and after, and start again from the kept
          one anytime.
        </HelpCallout>
        <SupportLine />
      </>
    );
  }

  return (
    <>
      <HelpCards items={BRIEF} columns={2} />

      <HelpSection title="Try a brief" icon={Wand2}>
        <p className="text-sm text-slate-600">
          Start from a combo or tap styles to see exactly what Design Studio hears. Only the styles that fit the post
          and format show up, and on New design you can also upload a reference image to match a style.
        </p>
        <BriefBuilder {...builder} />
      </HelpSection>

      <BackgroundsLine onNavigate={onNavigate} refining={false} />

      <HelpCallout>
        Nothing is final until you approve it. Refine one slide or all of them, in the same plain words.
      </HelpCallout>
    </>
  );
};
